import { Autocomplete, IconButton, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { DeleteOutline } from "@mui/icons-material";
import { UOM_LIST } from "../helper";
import { toast } from "react-toastify";

const AddItemModal = ({
  poItem,
  lineItem,
  updateItem,
  onDelete,
  vendorCode,
  isItemDeleted,
  prQuantity,
  isPRLinked = false,
  poDate,
  rateContractMissing = false,
}) => {
  const [formDetails, setFormDetails] = useState({});
  const [items, setItems] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [prRequiredError, setPrRequiredError] = useState("");

  useEffect(() => {
    const { category = {} } = lineItem;
    if (category && category.id) {
      loadSubcategories(category);
    }
  }, []);

  useEffect(() => {
    if (!formDetails.item || isItemDeleted) {
      const newFormDetails = {
        ...poItem,
      };
      if (!newFormDetails.uom) {
        newFormDetails.uom = UOM_LIST[0];
      } else if (typeof newFormDetails.uom === "string") {
        newFormDetails.uom =
          UOM_LIST.find((item) => item.value === newFormDetails.uom) || null;
      }

      setFormDetails(newFormDetails);
    }
  }, [poItem, isItemDeleted]);

  const loadItems = (subcategory) => {
    const { id = "" } = subcategory || {};
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_ITEMS_BY_VENDOR}`,
      null,
      {
        page: 1,
        subCategory: id,
        vendorCode,
        limit: 1000,
        excludeRateContractRequired: true,
        ...(poDate ? { poDate } : {}),
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setItems(response || []);
        }
      })
      .catch((err) => {});
  };

  const loadSubcategories = (category) => {
    const { id = "" } = category || {};
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.SUBCATEGORY}`,
      null,
      {
        page: 1,
        category: id,
        limit: 1000,
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setSubcategories(response.results || []);
        }
      })
      .catch((err) => {});
  };

  const getTaxAmount = () => {
    const { rate = 0, quantity = 0, taxPercentage = 0 } = formDetails;
    return (rate * quantity * (taxPercentage / 100)).toFixed(2);
  };

  const getAmount = () => {
    const { rate = 0, quantity = 0 } = formDetails;
    return (rate * quantity).toFixed(2);
  };

  const getTotalAmount = () => {
    const { rate = 0, quantity = 0, taxPercentage = 0 } = formDetails;
    let taxAmount = rate * quantity * (taxPercentage / 100);
    return (rate * quantity + +taxAmount).toFixed(2);
  };

  const onItemChange = (item) => {
    if (item?.prRequired) {
      setPrRequiredError(
        "PR is required for this item. Please create a PR before creating a PO."
      );
      toast.error(
        "PR is required for this item. Please create a PR before creating a PO."
      );
      return;
    }
    if (item) {
      setPrRequiredError("");
      const isRateContractItem = !!item.rateContractRequired;
      const rate = isRateContractItem ? item.contractRate : item.price;
      const taxPercentage = isRateContractItem
        ? item.contractTaxPercentage
        : item.taxPercentage;
      // subCategory is intentionally NOT overwritten with item.subCategory here: that's
      // just the item's deprecated "primary" mirror, which can differ from the specific
      // subcategory the user picked in the dropdown above (an item can now have more
      // than one categoryMappings pair). formDetails.subCategory (already set by the
      // Subcategory Autocomplete's own onChange) is the correct, unambiguous selector.
      setFormDetails({
        ...formDetails,
        item: item,
        rate,
        refRate: item.price,
        quantity: 1,
        taxPercentage,
        hsnCode: item?.hsnCode || "",
        uom: UOM_LIST.find((uom) => item.uom == uom.value) || UOM_LIST[0],
        itemId: item.id,
        lineItemId: lineItem.id,
      });

      onFormChange({
        ...formDetails,
        item: item,
        rate,
        refRate: item.price,
        quantity: 1,
        taxPercentage,
        hsnCode: item?.hsnCode || "",
        uom: UOM_LIST.find((uom) => item.uom == uom.value) || UOM_LIST[0],
        itemId: item.id,
        lineItemId: lineItem.id,
      });
    } else {
      setPrRequiredError("");
      setFormDetails({
        ...formDetails,
        item: {},
        subCategory: {},
        rate: 0,
        refRate: 0,
        quantity: 0,
        taxPercentage: 0,
        hsnCode: "",
      });
      onFormChange({
        ...formDetails,
        subCategory: {},
        item: {},
        rate: 0,
        refRate: 0,
        quantity: 0,
        taxPercentage: 0,
        hsnCode: "",
      });
    }
  };

  const onInputChange = (event) => {
    setFormDetails({
      ...formDetails,
      [event.target.name]: event.target.value,
    });

    onFormChange({
      ...formDetails,
      [event.target.name]: event.target.value,
    });
  };

  const onFormChange = (newFormDetails) => {
    const {
      category = {},
      subCategory = {},
      item = {},
      uom = {},
    } = newFormDetails;

    updateItem({
      id: `${lineItem.id}-${category.id}-${item.id}`,
      mainCategoryName: category.name,
      mainCategoryId: category.id,
      // Category-mapping selector half -- see subCategory below. Without this the backend
      // never receives `category`, so it can't validate the {category, subCategory} pair
      // against the item's categoryMappings and falls back to (and fails on) the
      // single-mapping default for any multi-mapped item.
      category,
      subcategoryName: subCategory.name,
      subcategoryId: subCategory.id,
      subCategory,
      item,
      name: item.name,
      uom: uom.value,
      quantity: newFormDetails.quantity,
      rate: newFormDetails.rate,
      taxRate: newFormDetails.taxPercentage,
      taxPercentage: newFormDetails.taxPercentage,
      specification: item.specification,
      itemId: item.id,
      lineItemId: lineItem.id,
      refRate: item.price,
      lineItemName: lineItem.name,
      availableAmount: lineItem.availableAmount,
      description: newFormDetails.description,
      remarks: newFormDetails.remarks,
      hsnCode: newFormDetails.hsnCode || "",
      ticketNumber: newFormDetails.ticketNumber || "",
      ticketCreationDate: newFormDetails.ticketCreationDate || "",
    });
  };

  if (!isPRLinked) {
    return (
      <div
        className={`po-add-items-row ${
          lineItem?.category?.isTicketRequired && "ticket-required"
        } ${rateContractMissing ? "rate-contract-missing" : ""}`}
      >
        {rateContractMissing && (
          <span className="rate-contract-missing-indicator" style={{ color: "#c62828", fontSize: "0.8rem" }}>
            No active rate contract for selected vendor
          </span>
        )}
        <Autocomplete
          value={formDetails.subCategory || {}}
          onChange={(e, val) => {
            e.persist();
            if (val) {
              loadItems(val);
            }
            setFormDetails((prevDetails) => {
              const newDetails = {
                ...prevDetails,
                item: {},
                rate: 0,
                refRate: 0,
                quantity: 0,
                taxPercentage: 0,
                description: "",
                remarks: "",
                // Subcategory always resolves to exactly one parent category (enforced by
                // the Category/Subcategory master data), so its own populated `category` is
                // always an unambiguous category-mapping selector. Falls back to whatever
                // was already set so clearing the dropdown (val == null) doesn't wipe a
                // previously-resolved value.
                category: val?.category || prevDetails.category,
                subCategory: val || {},
                uom: "",
              };

              onFormChange(newDetails);

              return { ...newDetails };
            });
          }}
          disabled={rateContractMissing}
          getOptionLabel={(op) => op.name || ""}
          renderInput={(params) => (
            <TextField {...params} size="small" label="Subcategory" />
          )}
          options={subcategories}
        />

        <Autocomplete
          value={formDetails.item || {}}
          onChange={(e, val) => {
            onItemChange(val);
          }}
          disabled={rateContractMissing}
          getOptionLabel={(op) => `${op.itemCode || ""} - ${op.name || ""}`}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Select Item"
              error={!!prRequiredError}
              helperText={prRequiredError || ""}
            />
          )}
          options={items}
        />
        <Autocomplete
          value={formDetails.uom || ""}
          onChange={(e, val) => {
            setFormDetails({ ...formDetails, uom: val || "" });
            onFormChange({ ...formDetails, uom: val || "" });
          }}
          disabled={rateContractMissing}
          renderInput={(params) => (
            <TextField {...params} size="small" label="UOM" />
          )}
          options={UOM_LIST}
        />

        <TextField
          size="small"
          label="HSN Code"
          name="hsnCode"
          value={formDetails.hsnCode || ""}
          onChange={onInputChange}
          disabled={rateContractMissing}
        />

        <TextField
          size="small"
          label="Quantity"
          type="number"
          name="quantity"
          value={formDetails.quantity || ""}
          onChange={onInputChange}
          onFocus={(e) => e.target.select()}
          disabled={rateContractMissing}
        />
        <TextField
          size="small"
          label="Rate"
          type="number"
          name="rate"
          value={formDetails.rate || ""}
          onChange={onInputChange}
          disabled={rateContractMissing || !!formDetails.item?.rateContractRequired}
        />
        <TextField
          size="small"
          label="Amount"
          type="number"
          value={getAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Tax Rate"
          type="number"
          name="taxPercentage"
          value={
            formDetails?.hasOwnProperty("taxPercentage")
              ? formDetails.taxPercentage
              : ""
          }
          onChange={onInputChange}
          disabled={rateContractMissing || !!formDetails.item?.rateContractRequired}
        />

        <TextField
          size="small"
          label="Tax Amount"
          type="number"
          value={getTaxAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Ref Rate"
          type="number"
          value={(formDetails.item && formDetails.item.price) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Total Amount"
          type="number"
          value={getTotalAmount(formDetails) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Description"
          name="description"
          value={formDetails.description || ""}
          onChange={onInputChange}
          disabled={rateContractMissing}
        />
        <TextField
          size="small"
          label="Remarks"
          name="remarks"
          value={formDetails.remarks || ""}
          onChange={onInputChange}
          disabled={rateContractMissing}
        />
        {lineItem?.category?.isTicketRequired && (
          <>
            <TextField
              size="small"
              label="Ticket No."
              name="ticketNumber"
              value={formDetails.ticketNumber || ""}
              onChange={onInputChange}
              required
              disabled={rateContractMissing}
            />
            <TextField
              size="small"
              label="Ticket Creation Date"
              type="date"
              name="ticketCreationDate"
              value={
                formDetails.ticketCreationDate
                  ? new Date(formDetails.ticketCreationDate)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={onInputChange}
              InputLabelProps={{ shrink: true }}
              required
              disabled={rateContractMissing}
            />
          </>
        )}
        <IconButton
          disabled={lineItem.availableAmount <= 0 || rateContractMissing}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(lineItem);
          }}
        >
          <DeleteOutline color="primary" />
        </IconButton>
      </div>
    );
  }

  return (
    <div className={`po-item-card${rateContractMissing ? " rate-contract-missing" : ""}`}>
      <div className="po-item-header">
        <span className="po-item-title">
          {formDetails.item?.name || formDetails.item?.itemCode || "New Item"}
        </span>
        {rateContractMissing && (
          <span className="rate-contract-missing-indicator" style={{ color: "#c62828", fontSize: "0.8rem" }}>
            No active rate contract for selected vendor
          </span>
        )}
        <IconButton
          className="delete-button"
          disabled={lineItem.availableAmount <= 0}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(lineItem);
          }}
        >
          <DeleteOutline color="primary" />
        </IconButton>
      </div>

      <div className="po-item-row item-info-row">
        {isPRLinked ? (
          <TextField
            size="small"
            label="Subcategory"
            value={formDetails.subCategory?.name || formDetails.subcategoryName || ""}
            disabled
          />
        ) : (
          <Autocomplete
            value={formDetails.subCategory || {}}
            onChange={(e, val) => {
              e.persist();
              if (val) {
                loadItems(val);
              }
              setFormDetails((prevDetails) => {
                const newDetails = {
                  ...prevDetails,
                  item: {},
                  rate: 0,
                  refRate: 0,
                  quantity: 0,
                  taxPercentage: 0,
                  description: "",
                  remarks: "",
                  subCategory: val || {},
                  uom: "",
                };

                onFormChange(newDetails);

                return { ...newDetails };
              });
            }}
            getOptionLabel={(op) => op.name || ""}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Subcategory" />
            )}
            options={subcategories}
          />
        )}

        {isPRLinked ? (
          <TextField
            size="small"
            label="Select Item"
            value={
              formDetails.item?.itemCode
                ? `${formDetails.item.itemCode} - ${formDetails.item.name}`
                : formDetails.item?.name || formDetails.name || ""
            }
            disabled
          />
        ) : (
          <Autocomplete
            value={formDetails.item || {}}
            onChange={(e, val) => {
              onItemChange(val);
            }}
            getOptionLabel={(op) => `${op.itemCode || ""} - ${op.name || ""}`}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Select Item" />
            )}
            options={items}
          />
        )}
        {isPRLinked ? (
          <TextField
            size="small"
            label="UOM"
            value={formDetails.uom?.label || poItem?.uom || ""}
            disabled
          />
        ) : (
          <Autocomplete
            value={formDetails.uom || ""}
            onChange={(e, val) => {
              setFormDetails({ ...formDetails, uom: val || "" });
              onFormChange({ ...formDetails, uom: val || "" });
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="UOM" />
            )}
            options={UOM_LIST}
          />
        )}

        <TextField
          size="small"
          label="HSN Code"
          name="hsnCode"
          value={formDetails.hsnCode || ""}
          onChange={onInputChange}
          disabled={isPRLinked}
        />
      </div>

      <div
        className={`po-item-row pricing-row${
          lineItem?.category?.isTicketRequired ? " has-ticket-fields" : ""
        }`}
      >
        <TextField
          size="small"
          label="Balance PR Quantity"
          type="number"
          value={prQuantity !== undefined ? prQuantity : ""}
          disabled
        />

        <TextField
          size="small"
          label="PO Quantity"
          type="number"
          name="quantity"
          value={formDetails.quantity || ""}
          onChange={(e) => {
            const raw = e.target.value;
            // raw is always the input's full current value (React/DOM contract), so this
            // already validates the complete typed number, never a partial keystroke -- the
            // "42 accepted when balance is 5" symptom was the *previous* value never getting
            // replaced (see onFocus below), not this check reading a partial value.
            const clamped =
              prQuantity !== undefined && Number(raw) > prQuantity
                ? String(prQuantity)
                : raw;
            onInputChange({ target: { name: "quantity", value: clamped } });
          }}
          // Prefilled with the balance quantity by default -- without selecting it on focus,
          // clicking in and typing inserts at the cursor instead of replacing it (e.g. typing
          // "2" into a prefilled "4" produces "42" rather than "2"), which is what made
          // appended, out-of-intent values slip past the max check above.
          onFocus={(e) => e.target.select()}
          inputProps={{
            min: 0,
            ...(prQuantity !== undefined ? { max: prQuantity } : {}),
          }}
          // Still editable when the row already holds a quantity, so it can be reduced to fit
          // a 0 balance (the clamp above caps it) rather than being stuck over the PR balance.
          disabled={
            isPRLinked &&
            Number(prQuantity) === 0 &&
            !(Number(formDetails.quantity) > 0)
          }
        />
        <TextField
          size="small"
          label="Rate"
          type="number"
          name="rate"
          value={formDetails.rate || ""}
          onChange={onInputChange}
          disabled={isPRLinked && !rateContractMissing}
        />
        <TextField
          size="small"
          label="Amount"
          type="number"
          value={getAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Tax Rate"
          type="number"
          name="taxPercentage"
          value={
            formDetails?.hasOwnProperty("taxPercentage")
              ? formDetails.taxPercentage
              : ""
          }
          onChange={onInputChange}
          disabled={isPRLinked && !rateContractMissing}
        />

        <TextField
          size="small"
          label="Tax Amount"
          type="number"
          value={getTaxAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Ref Rate"
          type="number"
          value={(formDetails.item && formDetails.item.price) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Total Amount"
          type="number"
          value={getTotalAmount(formDetails) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Description"
          name="description"
          value={formDetails.description || ""}
          onChange={onInputChange}
          disabled={rateContractMissing}
        />
        <TextField
          size="small"
          label="Remarks"
          name="remarks"
          value={formDetails.remarks || ""}
          onChange={onInputChange}
          disabled={rateContractMissing}
        />
        {lineItem?.category?.isTicketRequired && (
          <>
            <TextField
              size="small"
              label="Ticket No."
              name="ticketNumber"
              value={formDetails.ticketNumber || ""}
              onChange={onInputChange}
              required
              disabled={rateContractMissing}
            />
            <TextField
              size="small"
              label="Ticket Creation Date"
              type="date"
              name="ticketCreationDate"
              value={
                formDetails.ticketCreationDate
                  ? new Date(formDetails.ticketCreationDate)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={onInputChange}
              InputLabelProps={{ shrink: true }}
              required
              disabled={rateContractMissing}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AddItemModal;
